import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Plus, 
  Users, 
  Crown, 
  UserMinus,
  Trash2,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamsPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`, { withCredentials: true });
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;
    
    try {
      await axios.post(`${API}/teams`, { name: newTeamName }, { withCredentials: true });
      toast.success(language === 'es' ? 'Equipo creado' : 'Team created');
      setIsCreateDialogOpen(false);
      setNewTeamName('');
      fetchTeams();
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error(language === 'es' ? 'Error al crear equipo' : 'Error creating team');
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !selectedTeam) return;
    
    try {
      await axios.post(`${API}/teams/${selectedTeam.team_id}/invite`, { email: inviteEmail }, { withCredentials: true });
      toast.success(language === 'es' ? 'Usuario invitado' : 'User invited');
      setIsInviteDialogOpen(false);
      setInviteEmail('');
      setSelectedTeam(null);
      fetchTeams();
    } catch (error) {
      console.error('Error inviting member:', error);
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al invitar' : 'Error inviting'));
    }
  };

  const handleRemoveMember = async (teamId, memberId) => {
    try {
      await axios.delete(`${API}/teams/${teamId}/members/${memberId}`, { withCredentials: true });
      toast.success(language === 'es' ? 'Miembro eliminado' : 'Member removed');
      fetchTeams();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error removing');
    }
  };

  const handleDeleteTeam = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/teams/${deleteTarget.team_id}`, { withCredentials: true });
      toast.success(language === 'es' ? 'Equipo eliminado' : 'Team deleted');
      setDeleteTarget(null);
      fetchTeams();
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const openInviteDialog = (team) => {
    setSelectedTeam(team);
    setIsInviteDialogOpen(true);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-lime-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in" data-testid="teams-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {t('myTeams')}
            </h1>
            <p className="text-zinc-400 mt-1">
              {language === 'es' ? 'Colabora con tu equipo compartiendo marcas e ideas' : 'Collaborate with your team sharing brands and ideas'}
            </p>
          </div>

          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-lime-500 text-black hover:bg-lime-600 font-medium gap-2"
                data-testid="create-team-btn"
              >
                <Plus className="w-4 h-4" />
                {t('createTeam')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {t('createTeam')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTeam} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-zinc-500">{t('teamName')}</Label>
                  <Input
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    placeholder={language === 'es' ? 'Mi equipo creativo' : 'My creative team'}
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-lime-500"
                    data-testid="team-name-input"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setIsCreateDialogOpen(false)}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    {t('cancel')}
                  </Button>
                  <Button 
                    type="submit"
                    className="bg-lime-500 text-black hover:bg-lime-600 font-medium"
                    data-testid="submit-team-btn"
                  >
                    {t('save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Teams List */}
        {teams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Users className="w-16 h-16 text-zinc-700 mb-4" />
            <p className="text-zinc-400 text-center mb-4">
              {language === 'es' ? 'No tienes equipos aún' : "You don't have teams yet"}
            </p>
            <Button 
              onClick={() => setIsCreateDialogOpen(true)}
              className="bg-lime-500 text-black hover:bg-lime-600 font-medium gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('createTeam')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {teams.map((team) => {
              const isOwner = team.owner_id === user?.user_id;
              
              return (
                <Card 
                  key={team.team_id}
                  className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors"
                  data-testid={`team-card-${team.team_id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-lime-500/10 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-lime-500" />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-semibold text-white">{team.name}</CardTitle>
                          <p className="text-xs text-zinc-500">
                            {team.members?.length || 0} {language === 'es' ? 'miembros' : 'members'}
                          </p>
                        </div>
                      </div>
                      {isOwner && (
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openInviteDialog(team)}
                            className="h-8 w-8 text-zinc-400 hover:text-lime-500"
                            data-testid={`invite-${team.team_id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => setDeleteTarget(team)}
                            className="h-8 w-8 text-zinc-400 hover:text-red-500"
                            data-testid={`delete-team-${team.team_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Owner */}
                    <div className="flex items-center gap-2 p-2 bg-zinc-800/30 rounded-lg">
                      <Crown className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm text-zinc-300">{t('owner')}</span>
                      {isOwner && (
                        <span className="text-xs text-lime-500 ml-auto">
                          ({language === 'es' ? 'Tú' : 'You'})
                        </span>
                      )}
                    </div>

                    {/* Members */}
                    {team.members && team.members.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium uppercase text-zinc-500">{t('members')}</p>
                        {team.members.map((memberId, idx) => (
                          <div 
                            key={memberId}
                            className="flex items-center justify-between p-2 bg-zinc-800/30 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <Avatar className="w-6 h-6">
                                <AvatarFallback className="bg-zinc-700 text-xs">
                                  {`M${idx + 1}`}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm text-zinc-300">{memberId}</span>
                            </div>
                            {isOwner && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveMember(team.team_id, memberId)}
                                className="h-6 w-6 text-zinc-400 hover:text-red-500"
                              >
                                <UserMinus className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Invite Dialog */}
        <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                {t('inviteMember')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleInviteMember} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase text-zinc-500">{t('email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-lime-500 pl-10"
                    data-testid="invite-email-input"
                  />
                </div>
                <p className="text-xs text-zinc-500">
                  {language === 'es' 
                    ? 'El usuario debe tener una cuenta en Brain-GL'
                    : 'The user must have a Brain-GL account'}
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsInviteDialogOpen(false)}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  {t('cancel')}
                </Button>
                <Button 
                  type="submit"
                  className="bg-lime-500 text-black hover:bg-lime-600 font-medium"
                  data-testid="submit-invite-btn"
                >
                  {t('inviteMember')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">
                {language === 'es' ? '¿Eliminar equipo?' : 'Delete team?'}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                {language === 'es' 
                  ? `Esta acción eliminará "${deleteTarget?.name}" y no se puede deshacer.`
                  : `This action will delete "${deleteTarget?.name}" and cannot be undone.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                {t('cancel')}
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteTeam}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppLayout>
  );
}
